import { useState, useEffect, useRef } from "react";
import { Bell, Package, Tag, Megaphone, Info, X, Trash2, CheckCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useMyOrders, useMyReviews, useNotifications, useActiveSiteOffers, useMyActiveOffers } from "@/lib/firestoreService";
import { findStatus } from "@/lib/seedData";
import { track } from "@/lib/analytics";
import { orderNumber } from "@/lib/orderNumber";

const TYPE_CFG = {
  offer:        { icon: Tag,       color: "text-[#556B2F]",  bg: "bg-[#556B2F]/10",  border: "border-l-[#556B2F]",  label: "Offer"     },
  broadcast:    { icon: Megaphone, color: "text-blue-600",   bg: "bg-blue-50",        border: "border-l-blue-400",   label: "Broadcast" },
  info:         { icon: Info,      color: "text-orange-600", bg: "bg-orange-50",      border: "border-l-orange-400", label: "Info"      },
  order_update: { icon: Package,   color: "text-[#5A2D1D]",  bg: "bg-[#EFD9B4]/60",  border: "border-l-[#5A2D1D]",  label: "Order"     },
};

function fmtTime(ms) {
  if (!ms) return "";
  return new Date(ms).toLocaleDateString("en-IN", {
    day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
  });
}

function orderNotif(order, reviewed) {
  const st = findStatus(order.status);
  const delivered = order.status === "delivered";
  return {
    id:          `order:${order.id}:${order.status}`,
    type:        "order_update",
    title:       `Order ${orderNumber(order)}`,
    body:        delivered && !reviewed
      ? "🎉 Delivered — tap to rate your order!"
      : (st ? `${st.icon} ${st.label}` : "Status updated."),
    link:        delivered ? "/my-orders" : `/order/${order.id}`,
    createdAtMs: (order.updatedAt?.seconds || order.createdAt?.seconds || 0) * 1000,
  };
}

function offerNotif(offer) {
  return {
    id:          `offer:${offer.id}`,
    type:        "offer",
    title:       `${offer.discount_percent}% off — ${offer.name}`,
    body:        offer.scope === "user" ? "A special offer just for you!" : "New site-wide offer available.",
    link:        "/offers",
    createdAtMs: offer.createdAt?.seconds ? offer.createdAt.seconds * 1000 : 0,
  };
}

function lsKey(uid, k) { return `notif_${k}_${uid}`; }

function getCleared(uid) {
  try { return JSON.parse(localStorage.getItem(lsKey(uid, "cleared")) || "{}"); } catch { return {}; }
}

export const NotificationPanel = () => {
  const { user } = useAuth();
  const orders       = useMyOrders(user?.id);
  const myReviews    = useMyReviews(user?.id);
  const globalNotifs = useNotifications();
  const siteOffers   = useActiveSiteOffers();
  const myOffers     = useMyActiveOffers(user?.id);

  const [open, setOpen]         = useState(false);
  const [showAll, setShowAll]   = useState(false);
  const [lastRead, setLastRead] = useState(() =>
    user ? parseInt(localStorage.getItem(lsKey(user.id, "lr")) || "0") : 0
  );
  const [snapRead, setSnapRead] = useState(lastRead);
  const [cleared, setCleared]   = useState(() => user ? getCleared(user.id) : {});
  const panelRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    const val = parseInt(localStorage.getItem(lsKey(user.id, "lr")) || "0");
    setLastRead(val);
    setSnapRead(val);
    setCleared(getCleared(user.id));
  }, [user]);

  const allNotifs = [
    ...globalNotifs.map(n => ({
      ...n,
      createdAtMs: n.createdAt?.seconds ? n.createdAt.seconds * 1000 : 0,
    })),
    ...(orders || []).map(o => orderNotif(o, Boolean(myReviews[o.id]))),
    ...siteOffers.map(offerNotif),
    ...myOffers.map(offerNotif),
  ]
    .filter(n => !cleared[n.id])
    .sort((a, b) => b.createdAtMs - a.createdAtMs);

  const unreadCount = allNotifs.filter(n => n.createdAtMs > lastRead).length;

  const markRead = () => {
    const now = Date.now();
    if (user) localStorage.setItem(lsKey(user.id, "lr"), String(now));
    setLastRead(now);
  };

  const clearOne = (id) => {
    const next = { ...cleared, [id]: true };
    setCleared(next);
    if (user) localStorage.setItem(lsKey(user.id, "cleared"), JSON.stringify(next));
  };

  const clearAll = () => {
    const next = { ...cleared };
    allNotifs.forEach(n => { next[n.id] = true; });
    setCleared(next);
    if (user) localStorage.setItem(lsKey(user.id, "cleared"), JSON.stringify(next));
  };

  const handleOpen = () => {
    setOpen(v => {
      if (!v) {
        setSnapRead(lastRead);
        setTimeout(markRead, 0);
        track("notification_panel_opened", { unread_count: unreadCount, total_count: allNotifs.length });
      }
      return !v;
    });
  };

  // Close dropdown on outside click (not modal)
  useEffect(() => {
    const handler = (e) => {
      if (!showAll && panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showAll]);

  // Lock body scroll when modal open
  useEffect(() => {
    document.body.style.overflow = showAll ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [showAll]);

  if (!user) return null;

  // Shared notification row renderer
  const NotifRow = ({ n, isNew, inModal }) => {
    const cfg  = TYPE_CFG[n.type] || TYPE_CFG.info;
    const Icon = cfg.icon;

    const content = (
      <div className={`group flex gap-3 px-4 py-3 border-l-[3px] ${cfg.border} ${isNew ? cfg.bg : ""} hover:bg-[#EFD9B4]/30 transition-colors relative`}>
        <div className={`flex-shrink-0 mt-0.5 w-7 h-7 rounded-full ${cfg.bg} grid place-items-center`}>
          <Icon className={`w-3.5 h-3.5 ${cfg.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm font-semibold text-[#2E1A12] leading-tight">{n.title}</p>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {isNew && <span className="w-2 h-2 rounded-full bg-red-500 mt-1" />}
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); clearOne(n.id); }}
                className="opacity-0 group-hover:opacity-100 p-0.5 text-[#2E1A12]/30 hover:text-red-500 transition-all"
                title="Clear notification"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          <p className="text-xs text-[#2E1A12]/65 mt-0.5 leading-relaxed">{n.body}</p>
          <p className="text-[10px] text-[#2E1A12]/35 mt-1">{fmtTime(n.createdAtMs)}</p>
        </div>
      </div>
    );

    return n.link ? (
      <Link
        key={n.id}
        to={n.link}
        onClick={() => {
          setOpen(false);
          if (inModal) setShowAll(false);
          track("notification_clicked", { notification_type: n.type, notification_id: n.id });
        }}
      >
        {content}
      </Link>
    ) : (
      <div key={n.id}>{content}</div>
    );
  };

  return (
    <>
      <div ref={panelRef} className="relative">
        {/* Bell button */}
        <button
          onClick={handleOpen}
          aria-label="Notifications"
          className="relative p-2 text-[#5A2D1D] hover:bg-[#5A2D1D]/10 rounded-sm transition-colors"
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold grid place-items-center leading-none">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>

        {/* Dropdown panel — fixed on mobile so it anchors to viewport edge instead of
            the small bell wrapper (which would push a 320px panel off-screen to the left) */}
        {open && (
          <div className="fixed inset-x-2 top-[5.25rem] sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 w-auto sm:w-96 bg-[#F5E6CC] border border-[#5A2D1D]/25 rounded-sm shadow-2xl z-50">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#5A2D1D]/15">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-[#556B2F]" />
                <h3 className="font-display font-bold text-[#5A2D1D]">Notifications</h3>
                {allNotifs.length > 0 && (
                  <span className="text-xs text-[#2E1A12]/40">{allNotifs.length}</span>
                )}
              </div>
              <div className="flex items-center gap-1">
                {allNotifs.length > 0 && (
                  <button
                    onClick={() => { clearAll(); setOpen(false); }}
                    className="p-1 text-[10px] font-bold text-[#2E1A12]/40 hover:text-red-500 transition-colors flex items-center gap-1"
                    title="Clear all"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
                <button
                  onClick={() => setOpen(false)}
                  className="p-1 hover:bg-[#5A2D1D]/10 rounded-sm text-[#5A2D1D]/50 hover:text-[#5A2D1D] transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* List — latest 5 */}
            <div className="max-h-[360px] overflow-y-auto divide-y divide-[#5A2D1D]/8">
              {allNotifs.length === 0 ? (
                <div className="py-10 text-center">
                  <Bell className="w-8 h-8 mx-auto mb-3 text-[#5A2D1D]/20" />
                  <p className="text-sm text-[#2E1A12]/50">No notifications</p>
                  <p className="text-xs text-[#2E1A12]/35 mt-1">Order updates & offers will appear here</p>
                </div>
              ) : allNotifs.slice(0, 5).map((n) => (
                <NotifRow key={n.id} n={n} isNew={n.createdAtMs > snapRead} />
              ))}
            </div>

            {/* Footer */}
            {allNotifs.length > 0 && (
              <div className="px-4 py-2.5 border-t border-[#5A2D1D]/10 flex items-center justify-between">
                <button
                  onClick={() => { setShowAll(true); setOpen(false); }}
                  className="text-xs font-semibold text-[#556B2F] hover:text-[#5A2D1D] transition-colors"
                >
                  View all notifications ({allNotifs.length}) →
                </button>
                {allNotifs.length > 0 && (
                  <button
                    onClick={() => { clearAll(); setOpen(false); }}
                    className="text-xs font-semibold text-red-500 hover:text-red-700 transition-colors flex items-center gap-1"
                  >
                    <Trash2 className="w-3 h-3" /> Clear all
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* All Notifications Modal */}
      {showAll && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#2E1A12]/50 backdrop-blur-sm">
          <div className="w-full max-w-lg bg-[#F5E6CC] border border-[#5A2D1D]/25 rounded-sm shadow-2xl flex flex-col max-h-[85vh]">
            {/* Modal header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#5A2D1D]/15 flex-shrink-0">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-[#556B2F]" />
                <h2 className="font-display text-lg font-bold text-[#5A2D1D]">All Notifications</h2>
                {allNotifs.length > 0 && (
                  <span className="text-xs bg-[#EFD9B4] border border-[#5A2D1D]/20 text-[#5A2D1D] font-bold px-2 py-0.5 rounded-full">
                    {allNotifs.length}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {allNotifs.length > 0 && (
                  <button
                    onClick={clearAll}
                    className="inline-flex items-center gap-1 text-xs font-bold text-red-500 hover:text-red-700 border border-red-200 hover:border-red-400 px-3 py-1.5 rounded-full transition-colors"
                  >
                    <Trash2 className="w-3 h-3" /> Clear All
                  </button>
                )}
                <button
                  onClick={() => setShowAll(false)}
                  className="p-1.5 hover:bg-[#5A2D1D]/10 rounded-sm text-[#5A2D1D]/50 hover:text-[#5A2D1D] transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal body */}
            <div className="overflow-y-auto flex-1 divide-y divide-[#5A2D1D]/8">
              {allNotifs.length === 0 ? (
                <div className="py-16 text-center">
                  <CheckCheck className="w-10 h-10 mx-auto mb-3 text-[#556B2F]/40" />
                  <p className="text-sm font-semibold text-[#2E1A12]/50">All caught up!</p>
                  <p className="text-xs text-[#2E1A12]/35 mt-1">No notifications to show</p>
                </div>
              ) : allNotifs.map((n) => (
                <NotifRow key={n.id} n={n} isNew={n.createdAtMs > snapRead} inModal />
              ))}
            </div>

            {/* Modal footer */}
            <div className="px-5 py-3 border-t border-[#5A2D1D]/10 flex-shrink-0 text-center">
              <button
                onClick={() => setShowAll(false)}
                className="text-sm font-semibold text-[#5A2D1D]/60 hover:text-[#5A2D1D] transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
